import { useEffect } from 'react';

interface WebVital {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}

export const PerformanceMonitor = () => {
  useEffect(() => {
    // Only run in production and if analytics consent is given
    if (process.env.NODE_ENV !== 'production') return;
    
    const sendToAnalytics = (metric: WebVital) => {
      // Send to Google Analytics if available
      if (typeof window !== 'undefined' && (window as unknown).gtag) {
        (window as unknown).gtag('event', metric.name, {
          event_category: 'Web Vitals',
          value: Math.round(metric.value),
          metric_rating: metric.rating,
          non_interaction: true,
        });
      }
      
      // Log to console in development
      console.log('Web Vital:', metric);
    };

    const getCLS = (onPerfEntry: (metric: WebVital) => void) => {
      import('web-vitals').then(({ onCLS }) => {
        onCLS((metric) => {
          const rating = metric.value <= 0.1 ? 'good' : metric.value <= 0.25 ? 'needs-improvement' : 'poor';
          onPerfEntry({
            name: 'CLS',
            value: metric.value,
            rating
          });
        });
      });
    };

    const getFID = (onPerfEntry: (metric: WebVital) => void) => {
      import('web-vitals').then(({ onINP }) => {
        // Use INP (Interaction to Next Paint) as FID replacement
        onINP((metric) => {
          const rating = metric.value <= 200 ? 'good' : metric.value <= 500 ? 'needs-improvement' : 'poor';
          onPerfEntry({
            name: 'INP',
            value: metric.value,
            rating
          });
        });
      });
    };

    const getFCP = (onPerfEntry: (metric: WebVital) => void) => {
      import('web-vitals').then(({ onFCP }) => {
        onFCP((metric) => {
          const rating = metric.value <= 1800 ? 'good' : metric.value <= 3000 ? 'needs-improvement' : 'poor';
          onPerfEntry({
            name: 'FCP',
            value: metric.value,
            rating
          });
        });
      });
    };

    const getLCP = (onPerfEntry: (metric: WebVital) => void) => {
      import('web-vitals').then(({ onLCP }) => {
        onLCP((metric) => {
          const rating = metric.value <= 2500 ? 'good' : metric.value <= 4000 ? 'needs-improvement' : 'poor';
          onPerfEntry({
            name: 'LCP',
            value: metric.value,
            rating
          });
        });
      });
    };

    const getTTFB = (onPerfEntry: (metric: WebVital) => void) => {
      import('web-vitals').then(({ onTTFB }) => {
        onTTFB((metric) => {
          const rating = metric.value <= 800 ? 'good' : metric.value <= 1800 ? 'needs-improvement' : 'poor';
          onPerfEntry({
            name: 'TTFB',
            value: metric.value,
            rating
          });
        });
      });
    };

    // Measure all Web Vitals
    getCLS(sendToAnalytics);
    getFID(sendToAnalytics);
    getFCP(sendToAnalytics);
    getLCP(sendToAnalytics);
    getTTFB(sendToAnalytics);

  }, []);

  return null; // This is a monitoring component with no UI
};

export default PerformanceMonitor;